import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { LexicalTypeaheadMenuPlugin } from "@lexical/react/LexicalTypeaheadMenuPlugin";
import { TextNode } from "lexical";
import { type FC, useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  $createChatReferenceBroadcastNode,
  type BroadcastRange,
  broadcastWords,
} from "../../nodes/broadcast-node";
import { BroadcastTypeaheadOption } from "../../types/broadcast-typeahead-option";
import { useBasicTypeaheadTriggerMatch } from "../../utils/use-basic-typeahead-trigger-match";
import { TypeaheadOptionsList } from "./typeahead-option-list";

const useBroadcastRangeLookupService = (searchText: string | null): BroadcastRange[] => {
  return useMemo(() => {
    if (searchText === null) {
      return [];
    }

    return broadcastWords.filter((word) => word.toLowerCase().startsWith(searchText.toLowerCase()));
  }, [searchText]);
};

export const BroadcastTypeaheadPlugin: FC = () => {
  const [queryString, setQueryString] = useState<string | null>(null);
  const results = useBroadcastRangeLookupService(queryString);
  const [editor] = useLexicalComposerContext();
  const onSelectOption = useCallback(
    (selected: BroadcastTypeaheadOption, nodeToReplace: TextNode | null, closeMenu: () => void) => {
      editor.update(() => {
        const chatReferenceBroadcastNode = $createChatReferenceBroadcastNode(selected.range);
        if (nodeToReplace) {
          nodeToReplace.replace(chatReferenceBroadcastNode);
        }
        closeMenu();
      });
    },
    [editor],
  );

  const options = useMemo(
    () => results.map((result) => new BroadcastTypeaheadOption(result)),
    [results],
  );

  const triggerFn = useBasicTypeaheadTriggerMatch("@", {
    minLength: 1,
  });

  return (
    <LexicalTypeaheadMenuPlugin<BroadcastTypeaheadOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      options={options}
      triggerFn={triggerFn}
      menuRenderFn={(anchorElementRef, renderProps) =>
        anchorElementRef.current && results.length
          ? createPortal(
              <TypeaheadOptionsList
                data-testid="broadcast-typeahead"
                {...renderProps}
                options={options}
                renderOption={(option) => <span>{option.display}</span>}
                sx={{
                  marginTop: 0,
                  minWidth: 120,
                }}
              />,
              anchorElementRef.current,
            )
          : null
      }
    />
  );
};
